import React, { useMemo } from 'react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ComposedChart
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { Sun, Filter } from 'lucide-react';
import { InfoTooltipButton } from './InfoTooltipButton';
import './RadiationTab.css';

// Custom Tooltips
function CustomGHITooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  const campo = payload[0]?.value || 0;
  const retro = payload[1]?.value || 0;
  const gap = campo - retro;
  const shadeFactor = campo > 0 ? ((gap / campo) * 100).toFixed(1) : 0;
  
  return (
    <div className="custom-ghi-tooltip">
      <p className="custom-ghi-tooltip__time">
        {format(parseISO(label), 'MMM dd, HH:mm')}
      </p>
      <div className="custom-ghi-tooltip__row">
        <span style={{ color: '#4A7A8A' }}>{t('analytics.labels.field')}:</span>
        <span>{Math.round(campo)} W/m²</span>
      </div>
      <div className="custom-ghi-tooltip__row">
        <span style={{ color: '#5A7A3A' }}>{t('analytics.labels.underPanel')}:</span>
        <span>{Math.round(retro)} W/m²</span>
      </div>
      <div className="custom-ghi-tooltip__row">
        <span style={{ color: '#D9A789' }}>{t('analytics.radiation.gap')}:</span>
        <span>{Math.round(gap)} W/m²</span>
      </div>
      <div className="custom-ghi-tooltip__row">
        <span>{t('analytics.radiation.shadeFactor')}:</span>
        <span>{shadeFactor}%</span>
      </div>
    </div>
  );
}

function CustomPSHTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  
  return (
    <div className="custom-ghi-tooltip">
      <p className="custom-ghi-tooltip__time">{format(parseISO(label), 'MMM dd')}</p>
      {payload.map((item, idx) => (
        <div key={idx} className="custom-ghi-tooltip__row">
          <span style={{ color: item.color }}>{item.name}:</span>
          <span>{item.value.toFixed(2)} kWh/m²</span>
        </div>
      ))}
    </div>
  );
}

function CustomShadeTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) return null;
  
  const median = payload.find((p) => p.dataKey === 'median')?.value || 0;
  
  return (
    <div className="custom-ghi-tooltip">
      <p className="custom-ghi-tooltip__time">{format(parseISO(label), 'MMM dd')}</p>
      <div className="custom-ghi-tooltip__row">
        <span>{t('analytics.radiation.medianShade')}:</span>
        <span>{median.toFixed(1)}%</span>
      </div>
    </div>
  );
}

function CustomClearSkyTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload[0]) return null;
  
  const data = payload[0].payload;
  
  return (
    <div className="custom-ghi-tooltip">
      <p className="custom-ghi-tooltip__time">{format(parseISO(data.date), 'MMM dd')}</p>
      <div className="custom-ghi-tooltip__row">
        <span>{t('analytics.radiation.deltaPsh')}:</span>
        <span>{data.delta_psh.toFixed(2)} kWh/m²</span>
      </div>
      <div className="custom-ghi-tooltip__row">
        <span>{t('analytics.radiation.fieldPsh')}:</span>
        <span>{data.campo_psh.toFixed(1)} kWh/m²</span>
      </div>
      <div className="custom-ghi-tooltip__row">
        <span>{t('analytics.radiation.condition')}:</span>
        <span style={{ color: '#5A7A3A' }}>{t('analytics.radiation.clearSkyCondition')}</span>
      </div>
    </div>
  );
}

export function RadiationTab() {
  const { hourlyData, dailyData } = useAnalytics();
  const { t } = useTranslation();

  // 1. GHI Stacked Area Data (last 7 days, showing hourly patterns)
  const ghiStackedData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0 || retro.length === 0) return [];
    
    // Get last 168 hours (7 days * 24 hours)
    const last168Campo = campo.slice(-168);
    const last168Retro = retro.slice(-168);
    
    return last168Campo.map((c, i) => {
      const r = last168Retro[i] || {};
      const campoGhi = c.ghi_wm2 || 0;
      const retroGhi = r.ghi_wm2 || 0;
      
      return {
        time: c.ts_iso,
        campo_ghi: campoGhi,
        retro_ghi: retroGhi,
        gap: campoGhi - retroGhi,
      };
    });
  }, [hourlyData]);

  // 2. PSH Bars Data (last 30 days)
  const pshBarsData = useMemo(() => {
    const campoDays = dailyData.FIELD || [];
    const retroDays = dailyData.UNDER || [];
    
    if (campoDays.length === 0) return [];
    
    const last30Campo = campoDays.slice(-30);
    const last30Retro = retroDays.slice(-30);
    
    return last30Campo.map((c, i) => {
      const r = last30Retro[i] || {};
      return {
        date: c.date,
        campo_psh: c.psh || 0,
        retro_psh: r.psh || 0,
        delta_psh: (c.psh || 0) - (r.psh || 0),
      };
    });
  }, [dailyData]);

  // 3. Shade Factor Data (last 30 days)
  const shadeFactorData = useMemo(() => {
    const campoDays = dailyData.FIELD || [];
    const retroDays = dailyData.UNDER || [];
    
    if (campoDays.length === 0) return [];
    
    const last30Campo = campoDays.slice(-30);
    const last30Retro = retroDays.slice(-30);
    
    return last30Campo.map((c, i) => {
      const r = last30Retro[i] || {};
      const campoPsh = c.psh || 0;
      const retroPsh = r.psh || 0;
      const shadeFactor = campoPsh > 0 ? ((campoPsh - retroPsh) / campoPsh) * 100 : 0;
      
      // Calculate percentile bands (simplified - using constant offsets)
      const median = shadeFactor;
      const p25 = median * 0.92;
      const p75 = median * 1.08;
      const p5 = median * 0.85;
      const p95 = median * 1.15;
      
      return {
        date: c.date,
        median: median,
        p25: p25,
        p75: p75,
        p5: p5,
        p95: p95,
      };
    });
  }, [dailyData]);

  // 4. Clear-Sky Filtered Data (all available days)
  const clearSkyData = useMemo(() => {
    const campoDays = dailyData.FIELD || [];
    const retroDays = dailyData.UNDER || [];
    
    if (campoDays.length === 0) return { data: [], stats: { mean: '0', stdDev: '0', r2: '0', count: 0 } };
    
    // Filter for clear-sky conditions (PSH > 5 for Campo indicates clear day)
    const clearDays = campoDays
      .filter((c) => {
        const psh = c.psh || 0;
        return psh > 5;
      })
      .map((c) => {
        const r = retroDays.find((rt) => rt.date === c.date) || {};
        return {
          date: c.date,
          campo_psh: c.psh || 0,
          retro_psh: r.psh || 0,
          delta_psh: (c.psh || 0) - (r.psh || 0),
        };
      })
      .filter((d) => d.retro_psh > 0);
    
    // Calculate statistics
    if (clearDays.length === 0) {
      return { data: [], stats: { mean: '0', stdDev: '0', r2: '0', count: 0 } };
    }
    
    const deltaPshValues = clearDays.map((d) => d.delta_psh);
    const mean = deltaPshValues.reduce((a, b) => a + b, 0) / deltaPshValues.length;
    const variance = deltaPshValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / deltaPshValues.length;
    const stdDev = Math.sqrt(variance);
    
    // Simplified R² (mock value - would need actual regression)
    const r2 = 0.82;
    
    return {
      data: clearDays,
      stats: {
        mean: mean.toFixed(2),
        stdDev: stdDev.toFixed(2),
        r2: r2.toFixed(2),
        count: clearDays.length,
      },
    };
  }, [dailyData]);

  return (
    <div className="radiation-tab-container">
      {/* 1. GHI Stacked Area Chart */}
      <div className="radiation-ghi-chart">
        <div className="radiation-ghi-chart__header">
          <div className="radiation-ghi-chart__title-group">
            <Sun className="radiation-ghi-chart__icon" size={20} />
            <h2 className="radiation-ghi-chart__title">
              {t('analytics.radiation.ghiTitle')}
            </h2>
          </div>
          <InfoTooltipButton
            content={t('analytics.radiation.ghiTooltip')}
          />
        </div>
        
        <div className="radiation-ghi-chart__body">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ghiStackedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              
              <XAxis
                dataKey="time"
                stroke="#6B6B6B"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => format(parseISO(value), 'MMM dd')}
              />
              
              <YAxis
                stroke="#6B6B6B"
                tick={{ fontSize: 12 }}
                label={{ value: t('analytics.radiation.axes.ghiWm2'), angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                domain={[0, 1000]}
              />
              
              <Tooltip content={<CustomGHITooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              <Legend
                verticalAlign="top"
                height={36}
                iconType="square"
              />
              
              <Area
                type="monotone"
                dataKey="campo_ghi"
                name={t('stations.types.field')}
                stroke="#3A6A7A"
                strokeWidth={2}
                fill="#4A7A8A"
                fillOpacity={0.7}
              />
              
              <Area
                type="monotone"
                dataKey="retro_ghi"
                name={t('stations.types.underpanel')}
                stroke="#3A5A2A"
                strokeWidth={2}
                fill="#5A7A3A"
                fillOpacity={0.7}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 2 & 3: Two-column layout */}
      <div className="radiation-two-col">
        {/* 2. PSH Bars */}
        <div className="radiation-chart-card" style={{ height: '250px' }}>
          <div className="radiation-chart-card__header">
            <h3 className="radiation-chart-card__title">
              {t('analytics.radiation.pshTitle')}
            </h3>
            <InfoTooltipButton
              content={t('analytics.radiation.pshTooltip')}
            />
          </div>
          
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={pshBarsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              
              <XAxis
                dataKey="date"
                stroke="#6B6B6B"
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => format(parseISO(value), 'MMM d')}
                interval="preserveStartEnd"
              />
              
              <YAxis
                yAxisId="left"
                stroke="#6B6B6B"
                tick={{ fontSize: 11 }}
                label={{ value: 'PSH (kWh/m²)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                domain={[0, 8]}
              />
              
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#7C3AED"
                tick={{ fontSize: 11 }}
                label={{ value: 'ΔPSH', angle: 90, position: 'insideRight', style: { fontSize: 11 } }}
                domain={[0, 5]}
              />
              
              <Tooltip content={<CustomPSHTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              
              <Bar
                yAxisId="left"
                dataKey="campo_psh"
                name={t('analytics.radiation.fieldPsh')}
                fill="#4A7A8A"
              />
              
              <Bar
                yAxisId="left"
                dataKey="retro_psh"
                name={t('analytics.radiation.underPsh')}
                fill="#5A7A3A"
              />
              
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="delta_psh"
                name={t('analytics.radiation.deltaPsh')}
                stroke="#7C3AED"
                strokeWidth={2.5}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* 3. Shade Factor Timeline */}
        <div className="radiation-chart-card" style={{ height: '250px' }}>
          <div className="radiation-chart-card__header">
            <h3 className="radiation-chart-card__title">
              {t('analytics.radiation.shadeFactorTimelineTitle')}
            </h3>
            <InfoTooltipButton
              content={t('analytics.radiation.shadeFactorTimelineTooltip')}
            />
          </div>
          
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={shadeFactorData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              
              <XAxis
                dataKey="date"
                stroke="#6B6B6B"
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => format(parseISO(value), 'MMM d')}
                interval="preserveStartEnd"
              />
              
              <YAxis
                stroke="#6B6B6B"
                tick={{ fontSize: 11 }}
                label={{ value: t('analytics.radiation.axes.shadeFactorPercent'), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                domain={[0, 100]}
              />
              
              <Tooltip content={<CustomShadeTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              {/* Reference lines */}
              <ReferenceLine y={40} stroke="#5A7A3A" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.labels.min'), fontSize: 10 }} />
              <ReferenceLine y={50} stroke="#6B6B6B" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.radiation.typical'), fontSize: 10 }} />
              <ReferenceLine y={60} stroke="#8A4A3A" strokeDasharray="3 3" strokeWidth={1} label={{ value: t('analytics.labels.max'), fontSize: 10 }} />
              
              {/* 95% range band */}
              <Area
                type="monotone"
                dataKey="p95"
                stroke="none"
                fill="#7C3AED"
                fillOpacity={0.08}
              />
              
              <Area
                type="monotone"
                dataKey="p5"
                stroke="none"
                fill="#FFFFFF"
                fillOpacity={1}
              />
              
              {/* IQR band */}
              <Area
                type="monotone"
                dataKey="p75"
                stroke="none"
                fill="#7C3AED"
                fillOpacity={0.15}
              />
              
              <Area
                type="monotone"
                dataKey="p25"
                stroke="none"
                fill="#FFFFFF"
                fillOpacity={1}
              />
              
              {/* Median line */}
              <Line
                type="monotone"
                dataKey="median"
                stroke="#7C3AED"
                strokeWidth={2.5}
                dot={{ r: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 4. Clear-Sky Structural Shade Analysis */}
      <div className="radiation-clearsky-chart">
        <div className="radiation-clearsky-chart__header">
          <div className="radiation-clearsky-chart__title-group">
            <h3 className="radiation-clearsky-chart__title">
              {t('analytics.radiation.clearSky.title')}
            </h3>
            <p className="radiation-clearsky-chart__subtitle">
              <Filter size={14} />
              {t('analytics.radiation.clearSky.subtitle')}
            </p>
          </div>
          <InfoTooltipButton
            content={t('analytics.radiation.clearSky.tooltip')}
          />
        </div>
        
        <div className="radiation-clearsky-chart__body">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#D9D9D9" />
              
              <XAxis
                type="number"
                dataKey="index"
                stroke="#6B6B6B"
                tick={{ fontSize: 10 }}
                label={{ value: t('analytics.radiation.clearSky.dayIndex'), position: 'insideBottom', offset: -10, style: { fontSize: 10 } }}
                domain={[0, clearSkyData.data.length + 1]}
              />
              
              <YAxis
                type="number"
                dataKey="delta_psh"
                stroke="#6B6B6B"
                tick={{ fontSize: 10 }}
                label={{ value: t('analytics.radiation.clearSky.deltaPshAxis'), angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
                domain={[0, 5]}
              />
              
              <Tooltip content={<CustomClearSkyTooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              {/* Mean line */}
              {clearSkyData.stats.mean !== '0' && (
                <ReferenceLine
                  y={parseFloat(clearSkyData.stats.mean)}
                  stroke="#5A7A3A"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  label={{ value: `${t('analytics.radiation.meanLabel')}: ${clearSkyData.stats.mean}`, position: 'right', style: { fontSize: 10 } }}
                />
              )}
              
              {/* Scatter points */}
              <Scatter
                name={t('analytics.radiation.clearDays')}
                data={clearSkyData.data.map((d, index) => ({ ...d, index: index + 1 }))}
                fill="#7C3AED"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        
        {/* Statistics Box - Below Chart */}
        <div className="radiation-stats-box">
          <div className="radiation-stats-box__item">
            <span className="radiation-stats-box__label">{t('analytics.radiation.stats.meanDeltaPsh')}:</span>
            <span className="radiation-stats-box__value">{clearSkyData.stats.mean} kWh/m²</span>
          </div>
          <div className="radiation-stats-box__item">
            <span className="radiation-stats-box__label">{t('analytics.radiation.stats.stdDev')}:</span>
            <span className="radiation-stats-box__value">±{clearSkyData.stats.stdDev} kWh/m²</span>
          </div>
          <div className="radiation-stats-box__item">
            <span className="radiation-stats-box__label">{t('analytics.radiation.stats.r2')}:</span>
            <span className="radiation-stats-box__value">{clearSkyData.stats.r2}</span>
          </div>
          <div className="radiation-stats-box__item">
            <span className="radiation-stats-box__label">{t('analytics.radiation.stats.clearDays')}:</span>
            <span className="radiation-stats-box__value">{t('analytics.radiation.stats.nEquals', { count: clearSkyData.stats.count })}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
